import React, { PropsWithChildren } from 'react'

import { ListItem } from '@ui/components/List'
import { TextColorType } from '@ui/design-system/colors/colors'
import { IconFCType } from '@ui/icons/types'

import { FireEmoji } from '@assets/emojis/Fire.emoji'

import { Box } from '@shared/components/Box'
import { RadioGroup, useRadioGroupContext } from '@shared/components/RadioGroup/RadioGroup'

export interface RadioBoxProps {
  label?: string | React.ReactNode
  checked?: boolean
  disabled?: boolean
  hint?: string
  hintColor?: TextColorType
  isUnselectedGray?: boolean
  value?: string | number
}

export interface RadioCardProps extends RadioBoxProps {
  logo?: IconFCType<{}>
  description?: string
  isOnFire?: boolean
}

export const RadioCard: React.FC<PropsWithChildren<RadioCardProps>> = (props) => {
  const { label, hint, hintColor, logo, description, value, isOnFire } = props

  const groupContext = useRadioGroupContext()

  const isDisabled = groupContext?.disabled ?? props.disabled ?? false
  const isChecked = groupContext ? groupContext.selectedValue === value : props.checked

  const handleClick = () => {
    if (isDisabled) {
      return
    }

    if (groupContext && value !== undefined) {
      groupContext.onChange(value)
    }
  }

  return (
    <ListItem
      size='md'
      startSlot={{ icon: logo, size: 'x40' }}
      subtitle={[
        ...(hint ? [{ text: hint, ...(hintColor ? { color: hintColor } : {}) }] : []),
        ...(description ? [{ text: description, color: 'text-low-em' as const }] : []),
      ]}
      endSlot={{ radio: true }}
      active={isChecked}
      disabled={isDisabled}
      onClick={handleClick}
    >
      {typeof label === 'string' ? (
        <Box flex align='center' gap='x4'>
          {label}
          {isOnFire && <FireEmoji size={16} />}
        </Box>
      ) : undefined}
    </ListItem>
  )
}

type CardOption = Omit<RadioBoxProps, 'onClick' | 'checked' | 'value'> & {
  id: string | number
  logo?: IconFCType<{}>
  description?: string
  isOnFire?: boolean
}

interface Props {
  value?: string | number
  onChange: (value: any) => void
  options: CardOption[]
}

export const ModalBodyCardSelect: React.FC<Props> = ({ value, onChange, options }) => {
  return (
    <RadioGroup value={value} onChange={onChange}>
      <div>
        <Box flex direction='column' gap='x16'>
          {options.map((option) => (
            <RadioCard
              logo={option.logo}
              description={option.description}
              isOnFire={option.isOnFire}
              label={option.label}
              hint={option.hint}
              hintColor={option.hintColor}
              disabled={option.disabled}
              value={option.id.toString()}
            />
          ))}
        </Box>
      </div>
    </RadioGroup>
  )
}
